/**
 * Decode a JWT payload without verification — scheduling only.
 * Never use this output for authorization decisions.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json =
      typeof atob === 'function'
        ? atob(padded)
        : typeof Buffer !== 'undefined'
          ? Buffer.from(padded, 'base64').toString('utf8')
          : null;
    if (!json) return null;
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Seconds until JWT `exp`, or null when missing / unparsable. */
export function getJwtExpiresInSeconds(token: string): number | null {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (typeof exp !== 'number') return null;
  return exp - Math.floor(Date.now() / 1000);
}

/** Refresh this many seconds before access-token expiry. */
export const PROACTIVE_REFRESH_LEAD_SECONDS = 120;

/** True when the access token is missing, expired, or inside the proactive refresh window. */
export function accessTokenNeedsRefresh(token: string | null | undefined): boolean {
  if (!token) return false;
  const expiresIn = getJwtExpiresInSeconds(token);
  if (expiresIn === null) return false;
  return expiresIn <= PROACTIVE_REFRESH_LEAD_SECONDS;
}
